import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Evaluation_Milestone, Milestone, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { EvaluationMilestoneForm } from 'src/screens/shared/evaluation-milestone/EvaluationMilestoneForm'
import { queryEvaluationMilestoneById } from 'src/screens/shared/evaluation-milestone/evaluationMilestonesQueries'
import { queryMilestoneById } from 'src/screens/shared/implementation/details/milestoneQueries'
import { usePermissionsForKapMilestones } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { PROJECT } from 'src/shared/constants/constants'
import { MILESTONE_RESPONSIBLE_TYPE_TYPE, MILESTONE_STATUS_TYPE } from 'src/shared/constants/milestone-constants'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

type SaveOrigin = 'SAVE' | 'SAVE_AND_BACK'

export const KapEvaluationMilestoneEditPage = () => {
  const { programId, milestoneId, milestonePath, evaluationMilestoneId } = useParams()
  const { getMessage } = useMessageSource()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const urqlClient = useClient()
  const originRef = useRef<SaveOrigin>('SAVE')

  const parsedProgramId = parseInt(programId as string)
  const parsedMilestoneId = parseInt(milestoneId as string)
  const parsedEvaluationMilestoneId = parseInt(evaluationMilestoneId as string)

  const [milestone, setMilestone] = useState<Milestone>()
  const [evaluationMilestone, setEvaluationMilestone] = useState<Evaluation_Milestone>()
  const [dataLoading, setDataLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setDataLoading(true)
      const [milestoneResult, evaluationMilestoneResult] = await Promise.all([
        urqlClient
          .query<{ milestone_by_pk: Query_Root['milestone_by_pk'] }, { id: number }>(queryMilestoneById, {
            id: parsedMilestoneId,
          })
          .toPromise(),
        urqlClient
          .query<{ evaluation_milestone_by_pk: Query_Root['evaluation_milestone_by_pk'] }, { id: number }>(
            queryEvaluationMilestoneById,
            { id: parsedEvaluationMilestoneId },
          )
          .toPromise(),
      ])

      if (milestoneResult.data?.milestone_by_pk && evaluationMilestoneResult.data?.evaluation_milestone_by_pk) {
        setMilestone(milestoneResult.data.milestone_by_pk)
        setEvaluationMilestone(evaluationMilestoneResult.data.evaluation_milestone_by_pk)
      } else {
        notificationService.operationFailed()
      }
      setDataLoading(false)
    }

    fetchData()
  }, [notificationService, parsedEvaluationMilestoneId, parsedMilestoneId, urqlClient])

  const { loading: permissionsLoading, canEdit } = usePermissionsForKapMilestones(
    parsedProgramId,
    milestone?.status as MILESTONE_STATUS_TYPE,
    milestone?.responsible_type as MILESTONE_RESPONSIBLE_TYPE_TYPE,
  )

  const loading = dataLoading || permissionsLoading
  const dossierId = milestone?.dossier_id

  const onBack = () => {
    const route = ROUTES.KapFinalReportMilestoneRoot.nested.Details.params({ programId, milestoneId, milestonePath })
    navigate(`${route}#${parsedEvaluationMilestoneId}`, {
      state: { expandedEvaluationMilestoneId: parsedEvaluationMilestoneId },
    })
  }

  const onSave = (origin: SaveOrigin) => {
    originRef.current = origin
    document
      .getElementById('evaluation-milestone-form')
      ?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const afterSubmit = () => {
    if (originRef.current === 'SAVE_AND_BACK') {
      onBack()
    }
  }

  if (!loading && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.edit.evaluation.milestone')}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={() => onSave('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={() => onSave('SAVE')} />
        </>
      }
      onBack={onBack}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        <>
          {!loading && (
            <>
              <HelpAndInstructions labelKey="label.help.project.evaluation.milestones.KAP" defaultExpansion />
              <EvaluationMilestoneForm
                dossierId={dossierId!}
                projectType={PROJECT.KAP}
                evaluationMilestone={evaluationMilestone}
                onSubmit={afterSubmit}
              />
            </>
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
